import { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from '@mantine/core'
import { useSupabaseClient } from '@supabase/auth-helpers-react'
import { startAuthentication, browserSupportsWebAuthn, browserSupportsWebAuthnAutofill } from '@simplewebauthn/browser'
import { IconFingerprint } from '@tabler/icons-react'

import { BACKOFFICE_API_URL } from '@/lib/constants'

export default function PasskeyLogin() {
    const supabaseClient = useSupabaseClient()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const autofillAbort = useRef(null)

    const verifyAssertion = useCallback(async (assertion) => {
        const verifyRes = await fetch(
            BACKOFFICE_API_URL + '/webauthn/authenticate',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(assertion),
            }
        )

        const data = await verifyRes.json()

        if (!verifyRes.ok) {
            throw new Error(data.error_message || 'Erreur d\'authentification')
        }

        const { error: sessionError } = await supabaseClient.auth.setSession({
            access_token: data.access_token,
            refresh_token: data.refresh_token,
        })

        if (sessionError) {
            throw sessionError
        }
    }, [supabaseClient])

    // Conditional UI: browser shows passkey suggestion automatically
    useEffect(() => {
        if (!browserSupportsWebAuthn()) return

        let cancelled = false

        const startConditionalUI = async () => {
            const supportsAutofill = await browserSupportsWebAuthnAutofill()
            if (!supportsAutofill || cancelled) return

            try {
                const optionsRes = await fetch(
                    BACKOFFICE_API_URL + '/webauthn/authenticate-options',
                    { credentials: 'include' }
                )
                const optionsJSON = await optionsRes.json()

                const abort = new AbortController()
                autofillAbort.current = abort

                const assertion = await startAuthentication({
                    optionsJSON,
                    useBrowserAutofill: true,
                    signal: abort.signal,
                })

                if (cancelled) return

                setLoading(true)
                await verifyAssertion(assertion)
            } catch (err) {
                if (cancelled || err.name === 'AbortError' || err.name === 'NotAllowedError') return
            }
        }

        startConditionalUI()

        return () => {
            cancelled = true
            autofillAbort.current?.abort()
        }
    }, [verifyAssertion])

    if (!browserSupportsWebAuthn()) {
        return null
    }

    const handlePasskeyLogin = async () => {
        autofillAbort.current?.abort()

        setLoading(true)
        setError(null)

        try {
            const optionsRes = await fetch(
                BACKOFFICE_API_URL + '/webauthn/authenticate-options',
                { credentials: 'include' }
            )
            const optionsJSON = await optionsRes.json()

            const assertion = await startAuthentication({ optionsJSON })

            await verifyAssertion(assertion)
        } catch (err) {
            if (err.name === 'NotAllowedError') {
                setError(null)
            } else {
                setError(err.message || 'Erreur d\'authentification')
            }
            setLoading(false)
        }
    }

    return (
        <div className="flex flex-col gap-2">
            <input
                type="text"
                autoComplete="webauthn"
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
            />

            <Button
                variant="light"
                color="dark"
                fullWidth
                leftSection={<IconFingerprint size={18} />}
                onClick={handlePasskeyLogin}
                loading={loading}
            >
                Connexion biométrique
            </Button>

            {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
            )}
        </div>
    )
}
